import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Container,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
    Chip,
    Stack,
    Divider,
} from "@mui/material";
import orderNow from '../../assets/background1.jpg';
import productApi from "../../services/productApi";
import Swal from "sweetalert2";
import {BUTTON_CANCEL_COLOR, BUTTON_SAVE_COLOR} from "../common/color";
import orderApi from "../../services/orderApi";
import categoriesApi from "../../services/categories";
import { useLocation } from 'react-router-dom';

function OrderNow() {
    const theme = useTheme();
    const location = useLocation();
    const selectedProduct = location.state?.selectedProduct || null;
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [categoryId, setCategoryId] = useState(selectedProduct?.categoryId || '');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedProductName, setSelectedProductName] = useState(null);
    
    const [formData, setFormData] = useState({
        productId: selectedProduct?.id || '',
        fullName: '',
        email: '',
        contactNo: '',
        location: '',
        remarks: '',
        isActive: true,
    });

    const [errors, setErrors] = useState({ 
        productId: '', 
        fullName: '', 
        email: '', 
        contactNo: '', 
        location: '', 
        remarks: '' 
    });

    const [touched, setTouched] = useState({ 
        productId: false, 
        fullName: false,
        email: false,
        contactNo: false,
        location: false,
        remarks: false 
    });

    useEffect(() => {
        if (selectedProduct) {
            setCategoryId(selectedProduct.categoryId);
            fetchAll(selectedProduct.categoryId, selectedProduct.id);
            setSelectedProductName(selectedProduct.prodName);
            
            // Find and set the category name
            const fetchCategoryName = async () => {
                try {
                    const response = await categoriesApi.getAll();
                    const category = response.find(cat => cat.id === selectedProduct.categoryId);
                    if (category) {
                        setSelectedCategory(category.catName);
                    }
                } catch (error) {
                    console.error('Error fetching categories:', error);
                }
            };
            
            fetchCategoryName();
        }
    }, [selectedProduct]);

    const validateField = (name, value) => {
        switch (name) {
            case 'productId': 
                return value ? '' : 'Product is required';
            case 'fullName': 
                return value.trim() ? '' : 'Name is required';
            case 'email': 
                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'Valid email is required';
            case 'contactNo': 
                return /^\d{10,15}$/.test(value) ? '' : 'Valid phone number (10-15 digits) is required';
            case 'location': 
                return value.trim().length >= 5 ? '' : 'Location must be at least 5 characters';
            case 'remarks': 
                return value.trim().length >= 10 ? '' : 'Remarks must be at least 10 characters';
            default: 
                return '';
        }
    };

    useEffect(() => {
        const fetchAllCat = async () => {
            try {
                const response = await categoriesApi.getAll();
                setCategories(response);
            } catch (error) {
                console.error('Fetch error:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAllCat();
    }, []);

    const handleChangeProd = async (e) => {
        const value = e.target.value;
        setCategoryId(value);
        
        // Find and set the category name
        const category = categories.find(cat => cat.id === value);
        if (category) {
            setSelectedCategory(category.catName);
        } else {
            setSelectedCategory(null);
        }
        
        setSelectedProductName(null);
        await fetchAll(value);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'productId') {
            const product = products.find(prod => prod.id === value);
            if (product) {
                setSelectedProductName(product.prodName);
            }
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Validate immediately when changing
        if (touched[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: validateField(name, value)
            }));
        }
    };

    const fetchAll = async (catId, productId = '') => {
        try {
            const response = await productApi.getAll();
            const filteredProducts = response.data.filter(product => product.category.id === catId);
            setProducts(filteredProducts);
            
            if (productId) {
                setFormData(prev => ({
                    ...prev,
                    productId: productId,
                }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    productId: '',
                }));
                setSelectedProductName(null);
            }
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {
            productId: validateField('productId', formData.productId),
            fullName: validateField('fullName', formData.fullName),
            email: validateField('email', formData.email),
            contactNo: validateField('contactNo', formData.contactNo),
            location: validateField('location', formData.location),
            remarks: validateField('remarks', formData.remarks),
        };
        
        setErrors(newErrors);
        setTouched({
            productId: true,
            fullName: true,
            email: true,
            contactNo: true,
            location: true,
            remarks: true,
        });
        
        return !Object.values(newErrors).some(error => error);
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            Swal.fire('Validation Error', 'Please fill all required fields correctly', 'error');
            return;
        }

        const confirm = await Swal.fire({
            title: 'Confirm Order',
            text: `Are you sure you want to place this order?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: BUTTON_SAVE_COLOR,
            cancelButtonColor: BUTTON_CANCEL_COLOR,
            confirmButtonText: 'Yes, place order!'
        });

        if (!confirm.isConfirmed) return;

        setIsSubmitting(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('productId', formData.productId);
            formDataToSend.append('fullName', formData.fullName);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('contactNo', formData.contactNo);
            formDataToSend.append('location', formData.location);
            formDataToSend.append('remarks', formData.remarks);
            formDataToSend.append('isActive', formData.isActive);

            await orderApi.create(formDataToSend);

            // Reset form state
            setFormData({
                productId: '',
                fullName: '',
                email: '',
                contactNo: '',
                location: '',
                remarks: '',
                isActive: true
            });
            
            setErrors({});
            setTouched({});
            setCategoryId('');
            setSelectedCategory(null);
            setSelectedProductName(null);
            setProducts([]);

            Swal.fire({
                title: 'Success!',
                text: 'Your order has been placed successfully.',
                icon: 'success',
                confirmButtonColor: BUTTON_SAVE_COLOR
            });
        } catch (error) {
            console.error('Error:', error);
            Swal.fire({
                title: 'Error!',
                text: error.response?.data?.error || 'Failed to place order. Please try again.',
                icon: 'error'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (touched[name]) {
            setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    };

    return (
        <Box sx={{position: "relative"}}>
            <Box
                sx={{
                    height: isMobile ? "200px" : "300px",
                    minHeight: "300px",
                    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${orderNow})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundAttachment: isMobile ? "scroll" : "fixed",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    position: "relative",
                }}
            >
                <Container maxWidth="lg">
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            textAlign: "center",
                            px: isMobile ? 2 : 0,
                        }}
                    >
                        <Typography
                            variant={isMobile ? "h4" : "h3"}
                            component="h1"
                            sx={{
                                fontWeight: 700,
                                mb: 2,
                                textShadow: "2px 2px 4px rgba(0,0,0,0.5)",
                            }}
                        >
                            Order Now
                        </Typography>
                        <Typography
                            variant={isMobile ? "body1" : "h6"}
                            sx={{
                                maxWidth: "800px",
                                mx: "auto",
                                textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
                            }}
                        >
                            Complete the form below and our team will process your order promptly
                        </Typography>
                    </Box>
                </Container>
            </Box>

            {/* Form section */}
            <Container maxWidth="lg" sx={{py: 6}}>
                <Paper
                    elevation={3}
                    sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        mt: isMobile ? -8 : -12,
                        position: "relative",
                        zIndex: 2,
                        mx: isMobile ? 2 : 0,
                    }}
                >
                    <Grid container>
                        <Grid item xs={12} md={6} sx={{p: 4}}>
                            <Typography
                                variant="h5"
                                gutterBottom
                                sx={{
                                    fontWeight: 600,
                                    mb: 3,
                                }}
                            >
                                Order Details
                            </Typography>

                            {(selectedCategory || selectedProductName) && (
                            <Stack 
                                direction={{ xs: 'column', sm: 'row' }} 
                                spacing={1} 
                                sx={{ 
                                mb: 3,
                                alignItems: { xs: 'flex-start', sm: 'center' }
                                }}
                            >
                                {selectedCategory && (
                                <Chip 
                                    label={`Category: ${selectedCategory}`} 
                                    color="primary" 
                                    variant="outlined"
                                    sx={{
                                    mb: { xs: 1, sm: 0 },
                                    maxWidth: '100%',
                                    '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'block'
                                    }
                                    }}
                                />
                                )}
                                {selectedProductName && (
                                <Chip 
                                    label={`Product: ${selectedProductName}`} 
                                    color="secondary" 
                                    variant="outlined"
                                    sx={{
                                    maxWidth: '100%',
                                    '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'block'
                                    }
                                    }}
                                />
                                )}
                            </Stack>
                            )}

                            <Box
                                component="form"
                                sx={{
                                    "& .MuiFormControl-root": {mb: 2},
                                    "& .MuiButton-root": {
                                        py: 1.5,
                                        textTransform: "none",
                                        fontSize: "1rem",
                                    },
                                }}
                            >
                                <TextField
                                    label="Full Name *"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    error={touched.fullName && Boolean(errors.fullName)}
                                    helperText={touched.fullName && errors.fullName}
                                />
                                <TextField
                                    label="Email Address *"
                                    type="email"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    error={touched.email && Boolean(errors.email)}
                                    helperText={touched.email && errors.email}
                                />
                                <FormControl fullWidth size="small" sx={{ mb: 3 }} error={touched.productId && Boolean(errors.productId)}>
                                    <InputLabel>Category *</InputLabel>
                                    <Select
                                        name="categoryId"
                                        onChange={handleChangeProd}
                                        label="Category *"
                                        value={categoryId}
                                    >
                                        <MenuItem value="">
                                            <em>Select a category</em>
                                        </MenuItem>
                                        {categories.map((cat) => (
                                            <MenuItem key={cat.id} value={cat.id}>
                                                {cat.catName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth size="small" sx={{ mb: 3 }} error={touched.productId && Boolean(errors.productId)}>
                                    <InputLabel>Product *</InputLabel>
                                    <Select
                                        name="productId"
                                        value={formData.productId}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        label="Product *"
                                        disabled={!categoryId}
                                    >
                                        <MenuItem value="">
                                            <em>Select a product</em>
                                        </MenuItem>
                                        {products.map((prod) => (
                                            <MenuItem key={prod.id} value={prod.id}>
                                                {prod.prodName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {touched.productId && errors.productId && (
                                        <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                                            {errors.productId}
                                        </Typography>
                                    )}
                                </FormControl>
                                <TextField
                                    label="Phone Number *"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    name="contactNo"
                                    value={formData.contactNo}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    error={touched.contactNo && Boolean(errors.contactNo)}
                                    helperText={touched.contactNo && errors.contactNo}
                                />
                                <TextField
                                    label="Delivery Location *"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    error={touched.location && Boolean(errors.location)}
                                    helperText={touched.location && errors.location}
                                />
                                <TextField
                                    label="Special Instructions *"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    variant="outlined"
                                    size="small"
                                    placeholder="Please provide any special instructions for your order"
                                    name="remarks"
                                    value={formData.remarks}
                                    onChange={handleInputChange}
                                    onBlur={handleBlur}
                                    error={touched.remarks && Boolean(errors.remarks)}
                                    helperText={touched.remarks && errors.remarks}
                                />
                                <Button
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    sx={{mt: 3, py: 1.5}}
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Processing...' : 'Place Order'}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Container>
        </Box>
    );
}

export default OrderNow;